/**
 * WarningPanel
 * ============
 *
 * A coloured callout box for warnings (amber), informational notes (blue),
 * success messages (green), or errors (red).  Follows the existing info-box
 * pattern used throughout the informant management modals.
 *
 * Usage:
 *   <WarningPanel variant="warning">
 *     This informant has already started their questionnaires.
 *   </WarningPanel>
 */

import React from 'react';
import { ExclamationIcon } from '@heroicons/react/outline';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/solid';

const VARIANTS = {
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    Icon: ExclamationIcon,
    iconColor: 'text-amber-500',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    Icon: InformationCircleIcon,
    iconColor: 'text-blue-500',
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    Icon: CheckCircleIcon,
    iconColor: 'text-green-500',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    Icon: XCircleIcon,
    iconColor: 'text-red-500',
  },
};

/**
 * @param {Object}       props
 * @param {'warning'|'info'|'success'|'error'} props.variant – Visual style
 * @param {React.ReactNode} props.children                    – Content
 * @param {string}       [props.className]                    – Extra classes
 * @param {string}       [props.role]                         – ARIA role (defaults to 'alert' for error/warning)
 */
export default function WarningPanel({
  variant = 'info',
  children,
  className = '',
  role,
}) {
  const v = VARIANTS[variant] || VARIANTS.info;
  const defaultRole = variant === 'error' || variant === 'warning' ? 'alert' : 'status';

  const resolvedRole = role || defaultRole;
  const ariaLive = resolvedRole === 'alert' ? 'assertive' : 'polite';

  return (
    <div
      role={resolvedRole}
      aria-live={ariaLive}
      className={`flex gap-2.5 p-3 border rounded-md text-xs ${v.bg} ${v.border} ${v.text} ${className}`}
    >
      <v.Icon className={`w-4 h-4 shrink-0 mt-0.5 ${v.iconColor}`} aria-hidden="true" />
      <div className="flex-1">{children}</div>
    </div>
  );
}
